import { Box, Link } from "@mui/material";

import { faqs, howItWorks } from "./helpers";
import { routes } from "@/global/routes";
import { vendorUUIDs } from "../../constants";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";

import type { TeamConfig } from "@/global/hooks/useTeamConfig";

import AuthBgBanner from "public/images/devils/devils.png";
import SeatMap3dPreview from "public/images/devils/devils_flex_3d_venue.png";
import FindGamesBanner from "public/images/devils/1486x148.png";

const logoImage =
  "https://ss-leagues.s3.amazonaws.com/NHL_2024/NewJersey-Devils-2024.jpg";

const config: TeamConfig = {
  teamSchema: "devils_pickem",
  browserTitle: "Devils Pick 'Em Plan",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  manageCard: true,
  teamName: "New Jersey Devils",

  packs: {
    enabled: true,
    dontOpenPackModal: true,
    list: [
      {
        id: 1,
        amount: 7,
        title: <Box mt={2}>7 Game Pack</Box>,
        description: (
          <Box mb={2}>Choose a total of 7 games across two tiers!</Box>
        ),
        offerMoreMessage: "",
        priritizePackAmountForEventCount: true,
        scrollToTopWhenSelecting: true,
        buckets: [
          {
            id: 1,
            title: "Choose One (1) Game",
            description: "",
            max: 1,
            min: 0,
            closeWhenHitMax: true,
          },
          {
            id: 2,
            title: "Choose Min Six (6) Games",
            description: "",
            max: 7,
            min: 0,
            dontCloseWhenHitMin: true,
          },
        ],
        updatedAccountGroup: 0,
        successMessage:
          "You're good to go! Add more games or select Continue to view seating options.",
      },
      {
        id: 2,
        amount: 14,
        title: "14 Game Pack",
        description:
          "Choose a total of 14 games, no restrictions! Rangers included. Includes access to new, exclusive benefits package!",
        offerMoreMessage: "",
        defaultSelected: true,
        priritizePackAmountForEventCount: true,
        scrollToTopWhenSelecting: true,
        buckets: [
          {
            id: 1,
            title: "Pick 14 games",
            description: "",
            max: 14,
            min: 14,
            dontCloseWhenHitMin: true,
          },
        ],
        updatedAccountGroup: 1,
        successMessage:
          "You're good to go! Select Continue to view seating options.",
      },
    ],
  },

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-10215-devils-sec",
    mapId: "main",
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.purchaseSeats,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL",

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 7,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    checkoutTimerEnabled: false,
    showFrontendTotalAmount: false,
    showFrontendServiceFee: false,

    hideViewTicketsInThankYouPage: false,
    defaultSeatCount: 2,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
    checkoutTimerEnabled: false,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },
  isSaveCardAllowed: true,

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "G-XT9NZ1847J",
    attrkPixelId: "",
    beaconId: "",
    metaPixelId: "120085088485875",
    stackAdaptPixelId: "pmYksymwpGkOLia4azzaJQ",
  },

  // call refresh inventory when selecting games
  refreshInventoryWhenEventClick: false,

  shouldCheckZipCode: false,

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/devils/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",

  // images
  images: {
    findGamesBannerImage: FindGamesBanner.src,
    authBackgroundImage: AuthBgBanner.src,
    checkoutStatusPageImage: AuthBgBanner.src,
    logoImage: `${logoImage}?width=64&height=64`,
    logoNameImage: "",
    faviconImage:
      "https://ss-leagues.s3.amazonaws.com/NHL/NewJerseyDevils-aug21.png",
    seatMap3dPreviewImage: SeatMap3dPreview.src,
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: false,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    hideCheckoutCartBalance: true,
    customAdaLabel: (
      <>
        For accessible seating, please call the ticket office at{" "}
        <Link
          href="tel:9737576600"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (973) 757-6600
        </Link>
        .
      </>
    ),
  },

  colors: {
    primary: "#CE1126",
  },

  howItWorks,
  faqs,

  languages: {
    topNavMenuItems: "",
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: {
      progressBarAditionalText: " to continue!",
      progressBarSuccessText:
        "You're good to go! Add more events or select Continue to view seating options.",
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Thank you for your New Jersey Devils Pick 'Em purchase!",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },
    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
